/**
 * Types for graph visualization components
 */

import type { GraphNode } from "../../lib/api";

/** Node with position data for rendering */
export interface RenderedNode extends GraphNode {
  x?: number;
  y?: number;
  vx?: number;
  vy?: number;
  fx?: number | null;
  fy?: number | null;
  __indexColor?: string;
}

/** Link for force-graph (using source/target objects or strings) */
export interface RenderedLink {
  source: string | RenderedNode;
  target: string | RenderedNode;
  edge_type: string;
}

/** Color scheme for node types */
export const NODE_COLORS: Record<string, string> = {
  domain: "#6366f1",    // indigo-500
  subdomain: "#3b82f6", // blue-500
  ip: "#10b981",        // emerald-500
  port: "#8b5cf6",      // violet-500
};

/** Colors for node status */
export const STATUS_COLORS: Record<string, string> = {
  active: "#22c55e",    // green-500
  stale: "#eab308",     // yellow-500
  removed: "#ef4444",   // red-500
};

/** Colors for visibility classification */
export const VISIBILITY_COLORS: Record<string, string> = {
  known: "#3b82f6",     // blue-500
  unknown: "#f97316",   // orange-500
  rogue: "#ef4444",     // red-500
};

/** Colors for edge types */
export const EDGE_COLORS: Record<string, string> = {
  parent_of: "#94a3b8", // slate-400
  cname_to: "#f59e0b",  // amber-500
  contains: "#6366f1",  // indigo-500
};

/** Get node color based on type and status */
export function getNodeColor(node: GraphNode): string {
  // Risk-based coloring for high risk
  if (node.risk_score && node.risk_score >= 7) {
    return "#ef4444"; // red
  }
  if (node.risk_score && node.risk_score >= 4) {
    return "#f97316"; // orange
  }

  // Type-based coloring
  return NODE_COLORS[node.node_type] || "#6b7280";
}

/** Get node size based on type and connections */
export function getNodeSize(node: GraphNode): number {
  const baseSizes: Record<string, number> = {
    domain: 12,
    subdomain: 8,
    ip: 8,
    port: 5,
  };

  const baseSize = baseSizes[node.node_type] || 6;

  // Increase size for high-risk nodes
  if (node.risk_score && node.risk_score >= 7) {
    return baseSize * 1.5;
  }

  return baseSize;
}

/** Get node label for display */
export function getNodeLabel(node: GraphNode): string {
  // Keep labels short for readability
  const label = node.label;
  if (label.length > 30) {
    return label.slice(0, 27) + "...";
  }
  return label;
}

/** Format node properties for display */
export function formatNodeProperties(node: GraphNode): Array<{ key: string; value: string }> {
  const props = node.properties;
  const result: Array<{ key: string; value: string }> = [];

  // Common fields
  result.push({ key: "Type", value: node.node_type });
  result.push({ key: "Status", value: node.status });
  if (node.visibility) {
    result.push({ key: "Visibility", value: node.visibility });
  }
  if (node.risk_score !== undefined) {
    result.push({ key: "Risk Score", value: node.risk_score.toFixed(1) });
  }

  // Type-specific fields
  switch (node.node_type) {
    case "subdomain":
      if (props.fqdn) result.push({ key: "FQDN", value: String(props.fqdn) });
      if (props.cname_target) result.push({ key: "CNAME", value: String(props.cname_target) });
      if (props.takeover_risk) result.push({ key: "Takeover Risk", value: String(props.takeover_risk) });
      break;
    case "ip":
      if (props.ip_address) result.push({ key: "IP Address", value: String(props.ip_address) });
      if (props.asn_name) result.push({ key: "ASN", value: String(props.asn_name) });
      if (props.country_code) result.push({ key: "Country", value: String(props.country_code) });
      if (props.is_cloud) result.push({ key: "Cloud", value: String(props.cloud_provider || "Yes") });
      break;
    case "port":
      if (props.port) result.push({ key: "Port", value: String(props.port) });
      if (props.protocol) result.push({ key: "Protocol", value: String(props.protocol) });
      if (props.service_name) result.push({ key: "Service", value: String(props.service_name) });
      if (props.state) result.push({ key: "State", value: String(props.state) });
      break;
    case "domain":
      if (props.domain) result.push({ key: "Domain", value: String(props.domain) });
      break;
  }

  return result;
}
